import {
  AssignmentOutlined,
  BookOutlined,
  ChatBubbleOutline,
  HelpOutline,
  LogoutOutlined,
  ManageAccountsOutlined,
  MapOutlined,
  NewspaperOutlined,
} from '@mui/icons-material';
import { AppRouters } from '@/utils/constants/routers';
import { StyledList, UserButton, Wrapper } from './styles';
import { SidebarItem } from '@/components/molecules/sidebarItem';
import { Avatar, Box, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Text } from '@/components/atoms/text';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { truncateString } from '@/utils/functions/stringFormat';
import { TranslateButton } from '@/components/molecules/translateButton';
import { useTranslation } from '@/hooks/useTranslation';
import { UserRole } from '@/graphql/types/user';
import { BookIcon } from '@/components/atoms/customIcons/book';

export const SidebarItems = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const getIconColor = (path: string) => {
    return location.pathname === path ? theme.palette.primary.dark : theme.palette.action.active;
  };

  const navigateFromMarkdown = (markdownLink: string): string | null => {
    const regex = /\[.*?\]\((.*?)\)/;
    const match = markdownLink.match(regex);

    if (match && match[1]) {
      return match[1];
    }

    return null;
  };

  const openMarkdownLink = (markdownLink: string): void => {
    const url = navigateFromMarkdown(markdownLink);

    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <Wrapper>
      <StyledList>
        <SidebarItem
          icon={<MapOutlined sx={{ color: getIconColor(AppRouters.home) }} />}
          text={t('menu.menu.territorialDashboard')}
          path={AppRouters.home}
          onClick={() => navigate(AppRouters.home)}
        />

        <SidebarItem
          icon={<AssignmentOutlined sx={{ color: getIconColor(AppRouters.projectAnalysis) }} />}
          text={t('menu.menu.myProjects')}
          onClick={() => navigate(AppRouters.projectAnalysis)}
        />

        <SidebarItem
          icon={<BookOutlined sx={{ color: getIconColor(AppRouters.noExist) }} />}
          text={t('menu.menu.method')}
          onClick={() => {
            openMarkdownLink(t('menu.menu.method'));
          }}
        />

        <SidebarItem
          icon={<BookIcon sx={{ color: getIconColor(AppRouters.noExist) }} />}
          text={t('menu.menu.user_manual')}
          onClick={() => {
            openMarkdownLink(t('menu.menu.user_manual'));
          }}
        />

        <SidebarItem
          icon={<NewspaperOutlined sx={{ color: getIconColor(AppRouters.noExist) }} />}
          text={t('menu.menu.blog')}
          onClick={() => {
            openMarkdownLink(t('menu.menu.blog'));
          }}
        />

        <SidebarItem
          icon={<HelpOutline sx={{ color: getIconColor(AppRouters.noExist) }} />}
          text={t('menu.menu.faq')}
          onClick={() => {
            openMarkdownLink(t('menu.menu.faq'));
          }}
        />
      </StyledList>

      <Box>
        <Divider variant="fullWidth" />

        <UserButton
          data-testid="user-button"
          variant="text"
          onClick={() => navigate(AppRouters.profile)}
        >
          <Avatar src={user?.photoURL || ''} alt={user?.displayName || ''} />
          <Text variant="subtitle2" color="text.primary">
            {truncateString(user?.displayName || '', 20)}
          </Text>
        </UserButton>

        <Divider variant="fullWidth" />

        <Box sx={{ margin: theme.spacing(1, 0) }}>
          <TranslateButton />
        </Box>

        <Divider variant="fullWidth" />

        <StyledList>
          <SidebarItem
            icon={<ChatBubbleOutline color="action" />}
            text={t('menu.menu.contact')}
            onClick={() => {
              openMarkdownLink(t('menu.menu.contact'));
            }}
          />

          {user?.role === UserRole.ADMIN && (
            <SidebarItem
              dataTestId="admin-button"
              icon={<ManageAccountsOutlined sx={{ color: getIconColor(AppRouters.admin) }} />}
              text="Admin"
              path={AppRouters.admin}
              onClick={() => navigate(AppRouters.admin)}
            />
          )}

          <SidebarItem
            icon={<LogoutOutlined color="action" />}
            text={t('menu.menu.logout')}
            onClick={logout}
          />
        </StyledList>
      </Box>
    </Wrapper>
  );
};
